import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessEmailAddressInput from '../form-inputs/business-email-address-input/BusinessEmailAddressInput';
import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import AccountCircleIcon from '../../../assets/svg/account_circle.svg?react';
import BrushIcon from '../../../assets/svg/brush.svg?react';

import CircularProgress from '@mui/material/CircularProgress';
import { useEmployeeDetailsCardForm } from './useEmployeeDetailsCardForm';
import styles from './EmployeeDetailsCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Company } from '../../models/company.model';
import { Employee } from '../../models/employee.model';
import { toast, ToastContainer } from 'react-toastify';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { Label } from '@mui/icons-material';
import SincePill from './SincePill';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../message-modal/MessageModal';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { USER_PERMISSIONS } from '../constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';

type DetailsCardProps = {
  simplified?: boolean;
  company: Company;
  employee: Partial<Employee>;
};

enum MembershipState {
  NonMember,
  Pending,
  Member,
}

export default function DetailsCard({ simplified, employee }: DetailsCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const [invitation, setInvitation] = useState<any>(null);
  const [invitationSending, setInvitationSending] = useState<boolean>(false);
  const [member, setMember] = useState<any>(null);
  const [disableInvite, setDisableInvite] = useState(false);
  const [bothEmailsSet, setBothEmailsSet] = useState(false);
  const [membershipState, setMembershipState] = useState<MembershipState>(
    MembershipState.NonMember
  );

  const { invitations, organization, memberships } = useOrganization({
    invitations: true,
    memberships: true,
  });
  const { userId } = useAuth();
  const checkPermission = useCheckPermission();

  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useEmployeeDetailsCardForm(employee);

  useEffect(() => {
    setDisableInvite(
      (formData.email_address == null || formData.email_address.trim().length == 0) &&
        (formData.business_email_address == null ||
          formData.business_email_address.trim().length == 0)
    );
  }, [formData.email_address, formData.business_email_address]);

  const emailAddressLower = useMemo(
    () => formData.email_address?.toLowerCase() ?? '',
    [formData.email_address]
  );
  const businessEmailLower = useMemo(
    () => formData.business_email_address?.toLowerCase() ?? '',
    [formData.business_email_address]
  );

  useEffect(() => {
    const members = memberships?.data;
    const invitationList = invitations?.data;

    if (members) {
      const member = members.find(
        (mem) =>
          mem.publicUserData.identifier === emailAddressLower ||
          mem.publicUserData.identifier === businessEmailLower
      );
      if (member) {
        setMember(member);
        setMembershipState(MembershipState.Member);
        return;
      }
    }

    if (invitationList) {
      const invite = invitationList.find(
        (invite) =>
          invite.emailAddress.toLowerCase() === emailAddressLower ||
          invite.emailAddress.toLowerCase() === businessEmailLower
      );
      if (invite) {
        setInvitation(invite);
        setMembershipState(MembershipState.Pending);
        return;
      }
    }

    setMembershipState(MembershipState.NonMember);
  }, [memberships, invitations, emailAddressLower, businessEmailLower]);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);

  const handleInviteClick = () => {
    const bothEmailsSet =
      formData.email_address !== null &&
      formData.email_address.trim().length !== 0 &&
      formData.business_email_address !== null &&
      formData.business_email_address.trim().length !== 0;
    setBothEmailsSet(bothEmailsSet);
    if (!bothEmailsSet) {
      if (formData.email_address !== null && formData.email_address.trim().length !== 0) {
        setSelectedEmail(formData.email_address.toLowerCase());
      } else {
        setSelectedEmail(formData.business_email_address.toLowerCase());
      }
    }
    setIsInviteDialogOpen(true);
  };

  const handleRevokeInvitation = () => {
    if (invitation) {
      invitation.revoke();
      setTimeout(() => {
        window.location.reload(); // refreshes the page
      }, 1000); // 1 second = 1000 ms
    }
  };

  const handleCloseRevokeDialog = () => {
    setIsRevokeDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setIsInviteDialogOpen(false);
  };

  const handleSendInvite = () => {
    setInvitationSending(true);
    NWClient.sendInvitation({
      inviter: userId,
      organization: organization.id,
      target: selectedEmail,
    })
      .then(() => {
        toast.success(`${t('messages.invitationSentTo')} ${selectedEmail}`, {
          position: 'top-right',
        });
        window.location.reload();
      })
      .catch((reason) => {
        toast.error(
          `${t('messages.invitationTo')} ${selectedEmail} ${t(
            'messages.couldNotBeSent'
          )}: ${reason}`
        );
      })
      .finally(() => setInvitationSending(false));

    setIsInviteDialogOpen(false);
  };
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedEmail(value);
  };

  // const socialLinks: SocialLink[] = [
  //   // TODO: this will be replaced by endpoint response
  //   { key: 'website', name: 'website', url: 'https://company.com', icon: <WebIcon /> },
  //   { key: 'facebook', name: 'facebook', url: 'https://facebook.com', icon: <InstagramIcon /> },
  //   { key: 'twitter', name: 'twitter', url: 'https://twitter.com', icon: <InstagramIcon /> },
  //   { key: 'instagram', name: 'instagram', url: 'https://instagram.com', icon: <InstagramIcon /> },
  //   { key: 'linkedin', name: 'linkedin', url: 'https://linkedin.com', icon: <InstagramIcon /> },
  //   { key: 'youtube', name: 'youtube', url: 'https://youtube.com', icon: <InstagramIcon /> },
  // ];

  const [selectedEmail, setSelectedEmail] = useState('');
  const [invitationEmail, setInvitationEmail] = useState('');
  const [orgName, setOrgName] = useState('');

  useEffect(() => {
    if (invitation) {
      setInvitationEmail(invitation.emailAddress);
    }
    if (organization) {
      setOrgName(organization.name);
    }
  }, [invitation, organization]);

  return (
    <>
      <Grid container className='inputs-editable'>
        <Grid size={{ xs: 12 }}>
          <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
            <CardContent className={isLoading ? styles.card_content_min_height : ''}>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <Grid
                    container
                    alignItems='center'
                    paddingTop='0 !important'
                    paddingLeft='0 !important'
                    paddingRight='0 !important'
                    paddingBottom='16px !important'
                    minHeight='60px'
                    flexWrap='nowrap'
                    p={1}
                  >
                    <Grid size={{ xs: 11 }} container alignItems='center' gap={1}>
                      <Grid sx={{ marginBottom: '0.2rem' }}>
                        <AccountCircleIcon />
                      </Grid>
                      <Grid>
                        <Typography variant='h6'>
                          {t('businessProfile.detailsCardHeader')}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                      {!isEdited ? (
                        <Tooltip
                          className='flex-shrink-0'
                          title={
                            checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)
                              ? t('buttons.edit')
                              : t('messages.contactAdministrator')
                          }
                          placement='top'
                        >
                          <IconButton
                            onClick={toggleEditing}
                            disabled={
                              !checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)
                            }
                          >
                            <BrushIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Box display='flex' gap='8px' alignItems='center'>
                          <Button
                            type='button'
                            className='btn-default btn-action-basic btn-cancel'
                            role='button'
                            variant='contained'
                            size='large'
                            onClick={cancelChanges}
                          >
                            {t('buttons.cancel')}
                          </Button>
                          <Button
                            type='submit'
                            className='btn-default btn-action-basic'
                            role='button'
                            disabled={!isEdited}
                            variant='contained'
                            size='large'
                            onClick={saveChanges}
                          >
                            {t('buttons.save')}
                          </Button>
                        </Box>
                      )}
                    </Grid>
                  </Grid>

                  {!simplified ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <TextField
                              fullWidth
                              value={formData.full_name}
                              label='Full Name' // TODO : ADD TRANSLATION
                              onChange={(e) => handleChange('full_name')(e.target.value)}
                              disabled={!isEdited}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <BusinessEmailAddressInput
                              value={formData.email_address}
                              onChange={handleChange('email_address')}
                              label={t('labels.emailAddress')}
                              disabled={!isEdited}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <BusinessEmailAddressInput
                              value={formData.business_email_address}
                              onChange={handleChange('business_email_address')}
                              disabled={!isEdited}
                            />
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <BusinessPhoneNumberInput
                              value={formData.contact_number}
                              onChange={handleChange('contact_number')}
                              disabled={!isEdited}
                            />
                          </Grid>

                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <TextField
                              fullWidth
                              value={formData.address}
                              label={t('labels.physicalAddress')}
                              onChange={(e) => handleChange('address')(e.target.value)}
                              disabled={!isEdited}
                            />
                          </Grid>
                          {checkPermission(USER_PERMISSIONS.ORG_MEMBERSHIP_INVITE) && (
                            <Grid item xs={6} textAlign='center'>
                              {(() => {
                                switch (membershipState) {
                                  case MembershipState.NonMember:
                                    return (
                                      <Button
                                        className='btn-default btn-action-basic'
                                        onClick={handleInviteClick}
                                        disabled={isEdited || disableInvite}
                                      >
                                        {t('buttons.inviteEmployee')}
                                      </Button>
                                    );
                                  case MembershipState.Member:
                                    return (
                                      <SincePill
                                        startText={t('labels.memberSince')}
                                        memberSince={member.createdAt}
                                      />
                                    );
                                  case MembershipState.Pending:
                                    return invitation != null ? (
                                      <div>
                                        <SincePill
                                          startText={t('labels.pendingSince')}
                                          memberSince={invitation.createdAt}
                                        />
                                        <Button
                                          disabled={isEdited}
                                          className='btn-default btn-action-basic'
                                          onClick={() => setIsRevokeDialogOpen(true)}
                                          style={{ margin: '8px' }}
                                        >
                                          {t('buttons.revokeInvitation')}
                                        </Button>
                                      </div>
                                    ) : (
                                      <Label>{t('labels.pending')}</Label>
                                    );
                                }
                              })()}
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.full_name}
                          label='Full Name' // TODO : ADD TRANSLATION
                          onChange={(e) => handleChange('full_name')(e.target.value)}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <BusinessEmailAddressInput
                          value={formData.email_address}
                          onChange={handleChange('email_address')}
                          disabled={!isEdited}
                          label={t('labels.emailAddress')}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <BusinessEmailAddressInput
                          value={formData.business_email_address}
                          onChange={handleChange('business_email_address')}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <BusinessPhoneNumberInput
                          value={formData.contact_number}
                          onChange={handleChange('contact_number')}
                          disabled={!isEdited}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.address}
                          label={t('labels.physicalAddress')}
                          onChange={(e) => handleChange('address')(e.target.value)}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }} textAlign='center'>
                        {(() => {
                          switch (membershipState) {
                            case MembershipState.NonMember:
                              return (
                                <Button
                                  className='btn-default btn-action-basic'
                                  onClick={handleInviteClick}
                                  disabled={isEdited || disableInvite}
                                >
                                  {t('buttons.inviteEmployee')}
                                </Button>
                              );
                            case MembershipState.Member:
                              return (
                                <SincePill
                                  startText={t('labels.memberSince')}
                                  memberSince={member.createdAt}
                                />
                              );
                            case MembershipState.Pending:
                              return invitation != null ? (
                                <div>
                                  <SincePill
                                    startText={t('labels.pendingSince')}
                                    memberSince={invitation.createdAt}
                                  />
                                  <Button
                                    disabled={isEdited}
                                    className='btn-default btn-action-basic'
                                    onClick={() => setIsRevokeDialogOpen(true)}
                                    style={{ margin: '8px' }}
                                  >
                                    {t('buttons.revokeInvitation')}
                                  </Button>
                                </div>
                              ) : (
                                <Label>{t('labels.pending')}</Label>
                              );
                          }
                        })()}
                      </Grid>
                    </Grid>
                  )}

                  {
                    <Grid container className={styles.container_margin_top} alignItems='center'>
                      <Grid>
                        {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                      </Grid>
                    </Grid>
                  }
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        {isInviteDialogOpen && (
          <MessageModal
            open={isInviteDialogOpen}
            modalTitle={t('headers.invitation')}
            modalContent={
              bothEmailsSet ? (
                <div>
                  <Typography gutterBottom>{t('messages.whichEmailToUse')}</Typography>
                  <RadioGroup value={selectedEmail} onChange={handleEmailChange}>
                    {formData.email_address && (
                      <FormControlLabel
                        value={formData.email_address.toLowerCase()}
                        control={<Radio />}
                        label={formData.email_address.toLowerCase()}
                      />
                    )}
                    {formData.business_email_address && (
                      <FormControlLabel
                        value={formData.business_email_address.toLowerCase()}
                        control={<Radio />}
                        label={formData.business_email_address.toLowerCase()}
                      />
                    )}
                  </RadioGroup>
                </div>
              ) : (
                <Typography gutterBottom>
                  {t('messages.invitationWillBeSentTo')}
                  {selectedEmail}
                </Typography>
              )
            }
            actionButtonText={t('buttons.sendInvitation')}
            actionButtonFn={() => {
              handleCloseDialog();
              handleSendInvite();
            }}
            goBackButtonText={t('buttons.goBack')}
            onClose={handleCloseDialog}
            actionButtonDisabled={!selectedEmail}
          />
        )}
        {isRevokeDialogOpen && (
          <MessageModal
            open={isRevokeDialogOpen}
            modalMessage={
              t('messages.invitationRevokeMessage') +
              orgName +
              ' ' +
              t('miscellaneousText.for') +
              ' ' +
              formData.full_name +
              ' ' +
              invitationEmail +
              '?'
            }
            actionButtonText={t('buttons.revoke')}
            actionButtonFn={() => {
              handleCloseRevokeDialog();
              handleRevokeInvitation();
            }}
            goBackButtonText={t('buttons.goBack')}
            onClose={handleCloseRevokeDialog}
          />
        )}
        {/*<Dialog open={isInviteDialogOpen} onClose={handleCloseDialog}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {bothEmailsSet ? 'Select Email' : 'Invitation'}
          <IconButton
            aria-label='close'
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {bothEmailsSet ? (
            <div>
              <Typography gutterBottom>Which email to use?</Typography>
              <RadioGroup value={selectedEmail} onChange={handleEmailChange}>
                {formData.email_address && (
                  <FormControlLabel
                    value={formData.email_address}
                    control={<Radio />}
                    label={formData.email_address}
                  />
                )}
                {formData.business_email_address && (
                  <FormControlLabel
                    value={formData.business_email_address}
                    control={<Radio />}
                    label={formData.business_email_address}
                  />
                )}
              </RadioGroup>
            </div>
          ) : (
            <Typography gutterBottom>Invitation will be sent to {selectedEmail}</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => {
              console.log('Sending invite to:', selectedEmail);
              handleCloseDialog();
              handleSendInvite();
            }}
            variant='contained'
            disabled={!selectedEmail}
          >
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>*/}

        {/*<Dialog open={isRevokeDialogOpen} onClose={handleCloseRevokeDialog}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {'Are you sure you want to revoke the invitation to join organisation ' +
            orgName +
            ' for ' +
            formData.full_name +
            ' ' +
            invitationEmail +
            '?'}
          <IconButton
            aria-label='close'
            onClick={handleCloseRevokeDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogActions>
          <Button onClick={handleCloseRevokeDialog}>Cancel</Button>
          <Button
            onClick={() => {
              handleCloseRevokeDialog();
              handleRevokeInvitation();
            }}
            variant='contained'
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>*/}
        <ToastContainer />
        <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
        {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
      </Grid>
      {invitationSending && <ProgressOverlay />}
    </>
  );
}
